import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FileBarChart, Download, Trash2, History, Loader2, TrendingUp, Users as UsersIcon,
  Building2, Calendar as CalendarIcon, X, RefreshCw, FileText, ChevronRight, Clock, FileSpreadsheet,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ClientPdfDialog } from "@/components/reports/ClientPdfDialog";

const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
                 "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const ICONS = {
  muvekkil_monthly: Building2,
  personnel_prim: UsersIcon,
  tahsilat_trends: TrendingUp,
};

const TONE = {
  muvekkil_monthly: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", icon: "text-emerald-600", button: "bg-emerald-600 hover:bg-emerald-700" },
  personnel_prim: { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-900", icon: "text-indigo-600", button: "bg-indigo-600 hover:bg-indigo-700" },
  tahsilat_trends: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-900", icon: "text-sky-600", button: "bg-sky-600 hover:bg-sky-700" },
};

export default function ReportsTab() {
  const { isAdmin, role } = useAuth();
  const isMuhasebeci = role === "muhasebeci";
  const [catalog, setCatalog] = useState([]);
  const [history, setHistory] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState(null);
  const [filterType, setFilterType] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [c, h, s] = await Promise.all([
        api.get("/reports/catalog"),
        api.get(`/reports/history${filterType ? `?type=${filterType}` : ""}`),
        api.get("/reports/schedule").catch(() => ({ data: null })),
      ]);
      setCatalog((c.data?.reports || []).filter((r) => !(isMuhasebeci && r.id === "personnel_prim")));
      setHistory((h.data?.rows || []).filter((row) => !(isMuhasebeci && row.type === "personnel_prim")));
      setSchedule(s.data || null);
    } catch {
      toast.error("Rapor merkezi yüklenemedi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterType]);

  const openConfig = (r) => setConfiguring(r);

  const handleDeleteHistory = async (id, label) => {
    if (!window.confirm(`"${label}" raporunu geçmişten silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/reports/history/${id}`);
      toast.success("Rapor silindi");
      load();
    } catch {
      toast.error("Silinemedi");
    }
  };

  const handleReDownload = async (hist) => {
    try {
      const r = await api.get(`/reports/history/${hist.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = hist.filename || "rapor.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Rapor indirildi");
    } catch {
      toast.error("İndirme başarısız");
    }
  };

  return (
    <div className="space-y-5" data-testid="reports-tab">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-xl p-4 md:p-5 flex items-start md:items-center gap-4 flex-wrap">
        <div className="w-11 h-11 shrink-0 rounded-xl bg-white/15 grid place-items-center">
          <FileBarChart size={22} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h2 className="text-lg font-bold">Rapor Merkezi</h2>
          <p className="text-xs text-slate-300 mt-0.5">{isMuhasebeci ? "Müvekkil aylık özet ve tahsilat trendleri." : "Müvekkil aylık özet, personel prim raporu ve tahsilat trendleri."} Oluşturduğunuz raporlar geçmişte saklanır ve yeniden indirilebilir.</p>
        </div>
      </div>

      {/* Scheduling banner */}
      {schedule?.schedules?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3" data-testid="schedule-banner">
          <div className="w-9 h-9 shrink-0 rounded-lg bg-amber-100 grid place-items-center text-amber-700">
            <Clock size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-wider text-amber-700">Zamanlanmış Raporlar</div>
            {schedule.schedules.map((s) => (
              <div key={s.id} className="mt-1 text-sm text-amber-900">
                <strong>{s.label}</strong> · {s.cron_label} · <span className="text-amber-700">{s.scope}</span>
                {s.last_run ? (
                  <span className="ml-2 text-[11px] text-amber-700">
                    Son çalışma: {new Date(s.last_run.created_at).toLocaleString("tr-TR")}
                    {s.last_run.failed && <span className="ml-1 text-rose-700 font-semibold">· HATA</span>}
                  </span>
                ) : (
                  <span className="ml-2 text-[11px] text-amber-600 italic">Henüz çalışmadı</span>
                )}
              </div>
            ))}
            <div className="text-[11px] text-amber-700 mt-1 opacity-80">Oluşturulan raporlar aşağıdaki geçmiş listesinde "system@cron" olarak görünür.</div>
          </div>
        </div>
      )}

      {/* Report cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {loading && !catalog.length && (
          <div className="col-span-3 py-8 text-center text-slate-400 text-sm">
            <Loader2 size={16} className="inline animate-spin mr-1" /> Yükleniyor...
          </div>
        )}
        {catalog.map((r) => {
          const Icon = ICONS[r.id] || FileText;
          const tone = TONE[r.id] || TONE.muvekkil_monthly;
          return (
            <button
              key={r.id}
              onClick={() => openConfig(r)}
              data-testid={`report-card-${r.id}`}
              className={`text-left border rounded-xl p-4 hover:shadow-lg transition-all ${tone.bg} ${tone.border} ${tone.text} group`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 shrink-0 rounded-lg bg-white grid place-items-center ${tone.icon}`}>
                  <Icon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">{r.label}</div>
                  <div className="text-[11px] opacity-80 mt-1 leading-relaxed">{r.description}</div>
                </div>
                <ChevronRight size={16} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
              </div>
              <div className="mt-3 pt-3 border-t border-current/10 text-[11px] font-semibold opacity-70 flex items-center gap-1">
                <Download size={12} /> Rapor oluştur
              </div>
            </button>
          );
        })}
      </div>

      {/* History */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <History size={16} className="text-slate-600" />
          <h3 className="text-sm font-bold text-slate-900">Geçmiş Raporlar</h3>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            data-testid="history-filter"
            className="ml-auto px-2 py-1 border border-slate-300 rounded-md text-xs"
          >
            <option value="">Hepsi</option>
            {catalog.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
          <button
            onClick={load}
            data-testid="history-refresh"
            className="p-1.5 bg-slate-100 hover:bg-slate-200 rounded-md"
            title="Yenile"
          >
            <RefreshCw size={13} />
          </button>
        </div>
        <div className="sticky-table">
          <table className="w-full text-xs" data-testid="history-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-2 py-2 font-bold text-slate-700">Oluşturma</th>
                <th className="text-left px-2 py-2 font-bold text-slate-700">Tür</th>
                <th className="text-left px-2 py-2 font-bold text-slate-700">Dönem</th>
                <th className="text-right px-2 py-2 font-bold text-slate-700">Kayıt</th>
                <th className="text-right px-2 py-2 font-bold text-slate-700">Toplam</th>
                <th className="text-left px-2 py-2 font-bold text-slate-700">Oluşturan</th>
                <th className="text-right px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {!loading && history.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-400 py-6">Henüz rapor oluşturulmamış</td></tr>
              )}
              {history.map((h) => {
                const Icon = ICONS[h.type] || FileText;
                const tone = TONE[h.type] || TONE.muvekkil_monthly;
                return (
                  <tr key={h.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`history-row-${h.id}`}>
                    <td className="px-2 py-2 text-slate-700 text-[11px] font-mono">
                      {new Date(h.created_at).toLocaleString("tr-TR")}
                    </td>
                    <td className="px-2 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${tone.bg} ${tone.text} text-[10px] font-semibold`}>
                        <Icon size={11} /> {h.label}
                      </span>
                      <span data-testid={`history-format-${h.id}`} className={`ml-1 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${h.format === "xlsx" ? "bg-green-100 text-green-800" : "bg-rose-50 text-rose-700"}`}>
                        {h.format === "xlsx" ? "EXCEL" : "PDF"}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-slate-900 font-semibold">{h.summary?.label || "-"}</td>
                    <td className="px-2 py-2 text-right font-mono-tab text-slate-700">{h.summary?.count ?? "-"}</td>
                    <td className="px-2 py-2 text-right font-mono-tab text-slate-900 font-semibold">
                      {h.summary?.total != null ? Number(h.summary.total).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺" : "-"}
                    </td>
                    <td className="px-2 py-2 text-slate-600 text-[11px]">{h.created_by_email || "-"}</td>
                    <td className="px-2 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => handleReDownload(h)}
                          data-testid={`history-redownload-${h.id}`}
                          className="p-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded"
                          title="Yeniden indir"
                        >
                          <Download size={13} />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteHistory(h.id, h.label)}
                            data-testid={`history-delete-${h.id}`}
                            className="p-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded"
                            title="Sil"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {configuring && (
        <ConfigureModal
          report={configuring}
          onClose={() => setConfiguring(null)}
          onGenerated={() => { setConfiguring(null); load(); }}
        />
      )}
    </div>
  );
}


function ConfigureModal({ report, onClose, onGenerated }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [months, setMonths] = useState(12);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [generating, setGenerating] = useState("");

  const Icon = ICONS[report.id] || FileText;
  const tone = TONE[report.id] || TONE.muvekkil_monthly;
  const usesYearMonth = report.id === "muvekkil_monthly" || report.id === "personnel_prim";

  // Debounced preview
  useEffect(() => {
    const t = setTimeout(() => fetchPreview(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [year, month, months]);

  const fetchPreview = async () => {
    setLoadingPreview(true);
    try {
      let url = "";
      if (report.id === "muvekkil_monthly") url = `/reports/muvekkil-monthly/preview?year=${year}&month=${month}`;
      else if (report.id === "personnel_prim") url = `/reports/personnel-prim/preview?year=${year}&month=${month}`;
      else if (report.id === "tahsilat_trends") url = `/reports/tahsilat-trends/preview?months=${months}`;
      const r = await api.get(url);
      setPreview(r.data);
    } catch {
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleGenerate = async (format) => {
    const params = usesYearMonth ? { year, month } : { months };
    setGenerating(format);
    try {
      const r = await api.post("/reports/generate", { type: report.id, params, format }, { responseType: "blob" });
      const fname = (r.headers["x-report-filename"]) || `rapor.${format}`;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Rapor oluşturuldu ve indirildi");
      onGenerated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rapor oluşturulamadı");
    } finally {
      setGenerating("");
    }
  };

  const fmt = (v) => v == null ? "-" : Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const emptyState = useMemo(() => {
    if (!preview) return false;
    if (report.id === "muvekkil_monthly") return (preview.grand?.count || 0) === 0;
    if (report.id === "personnel_prim") return (preview.grand?.total_count || 0) === 0;
    if (report.id === "tahsilat_trends") return (preview.grand?.total_count || 0) === 0;
    return false;
  }, [preview, report.id]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="report-configure-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className={`p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10`}>
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg grid place-items-center ${tone.bg} ${tone.icon}`}>
              <Icon size={18} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">{report.label}</h3>
              <div className="text-[11px] text-slate-500">{report.description}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={14} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Params */}
          <div className={`${tone.bg} border ${tone.border} rounded-lg p-3`}>
            <div className={`text-[11px] font-bold uppercase tracking-wider ${tone.text} mb-2 flex items-center gap-1`}>
              <CalendarIcon size={12} /> Rapor Parametreleri
            </div>
            {usesYearMonth && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold text-slate-600 block mb-0.5">Yıl</label>
                  <select
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    data-testid="report-year"
                    className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
                  >
                    {[0, 1, 2, 3, 4].map((off) => {
                      const y = now.getFullYear() - off;
                      return <option key={y} value={y}>{y}</option>;
                    })}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-600 block mb-0.5">Ay</label>
                  <select
                    value={month}
                    onChange={(e) => setMonth(Number(e.target.value))}
                    data-testid="report-month"
                    className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
                  >
                    {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                  </select>
                </div>
              </div>
            )}
            {!usesYearMonth && (
              <div>
                <label className="text-[10px] font-bold text-slate-600 block mb-0.5">Süre (ay)</label>
                <div className="flex items-center gap-2">
                  {[6, 12, 24].map((n) => (
                    <button
                      key={n}
                      onClick={() => setMonths(n)}
                      data-testid={`months-${n}`}
                      className={`px-3 py-1.5 rounded-md text-sm font-semibold border transition-colors ${
                        months === n ? `${tone.button} text-white border-transparent` : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      Son {n} ay
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Preview */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-1">
              Önizleme
              {loadingPreview && <Loader2 size={11} className="animate-spin" />}
            </div>
            {!preview && !loadingPreview && (
              <div className="text-xs text-slate-400 py-4 text-center bg-slate-50 border border-slate-200 rounded-md">
                Veriler yüklenemedi
              </div>
            )}
            {preview && emptyState && (
              <div className="text-xs text-amber-800 py-4 text-center bg-amber-50 border border-amber-200 rounded-md" data-testid="preview-empty">
                Seçili dönem için kayıt bulunamadı · rapor boş olarak oluşturulacak
              </div>
            )}
            {preview && !emptyState && (
              <div className="bg-white border border-slate-200 rounded-md" data-testid="preview-panel">
                {report.id === "muvekkil_monthly" && (
                  <MuvekkilPreview data={preview} fmt={fmt} year={year} month={month} />
                )}
                {report.id === "personnel_prim" && (
                  <PersonnelPreview data={preview} fmt={fmt} />
                )}
                {report.id === "tahsilat_trends" && (
                  <TrendsPreview data={preview} fmt={fmt} />
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50 sticky bottom-0">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-semibold hover:bg-white">İptal</button>
          <button
            onClick={() => handleGenerate("xlsx")}
            disabled={!!generating}
            data-testid="report-generate-xlsx-btn"
            className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-semibold bg-green-700 hover:bg-green-800 disabled:opacity-50"
          >
            {generating === "xlsx" ? <><Loader2 size={14} className="animate-spin" /> Oluşturuluyor...</> : <><FileSpreadsheet size={14} /> Excel Oluştur + İndir</>}
          </button>
          <button
            onClick={() => handleGenerate("pdf")}
            disabled={!!generating}
            data-testid="report-generate-btn"
            className={`inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-semibold ${tone.button} disabled:opacity-50`}
          >
            {generating === "pdf" ? <><Loader2 size={14} className="animate-spin" /> Oluşturuluyor...</> : <><Download size={14} /> PDF Oluştur + İndir</>}
          </button>
        </div>
      </div>
    </div>
  );
}


function MuhasebeciVekaletToggle() {
  const [on, setOn] = useState(null);
  useEffect(() => {
    api.get("/tahsilat-settings/summary-flags").then((r) => setOn(!!r.data.roles.muhasebeci?.canSeeReportVekalet)).catch(() => {});
  }, []);
  if (on === null) return null;
  const toggle = async () => {
    try {
      await api.put("/tahsilat-settings/summary-flags", { role: "muhasebeci", flag: "canSeeReportVekalet", value: !on });
      setOn(!on);
      toast.success(`Muhasebeci vekalet ücretini ${!on ? "görebilir" : "göremez"}`);
    } catch { toast.error("Güncellenemedi"); }
  };
  return (
    <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 bg-white border border-slate-200 rounded-full px-2.5 py-1 cursor-pointer"
      title="Kullanıcı Yetkileri'nden de değiştirilebilir">
      <input type="checkbox" checked={on} onChange={toggle} data-testid="report-muhasebeci-vekalet-toggle" />
      Muhasebeci vekaleti görsün
    </label>
  );
}

function MuvekkilPreview({ data, fmt, year, month }) {
  const { isAdmin } = useAuth();
  const [pdfFor, setPdfFor] = useState(null);
  const [picked, setPicked] = useState([]);
  useEffect(() => { setPicked([]); }, [data]);
  const allPicked = data.summary.length > 0 && picked.length === data.summary.length;
  const togglePick = (m) => setPicked((p) => (p.includes(m) ? p.filter((x) => x !== m) : [...p, m]));
  return (
    <div className="divide-y divide-slate-100">
      <div className="p-3 bg-emerald-50 flex items-center gap-4 flex-wrap">
        <Stat label="Müvekkil" value={data.muvekkil_count} />
        <Stat label="Kayıt" value={data.grand.count} />
        <Stat label="Toplam Tahsilat" value={`${fmt(data.grand.tahsilat_total)} ₺`} big />
        {!data.vekalet_hidden && <Stat label="Toplam Vekalet" value={`${fmt(data.grand.vekalet_total)} ₺`} />}
        <Stat label="Toplam Gönderilecek" value={`${fmt(data.grand.gonderilecek_total)} ₺`} />
        {isAdmin && <MuhasebeciVekaletToggle />}
        <button onClick={() => setPdfFor("__picked__")} disabled={!picked.length} data-testid="client-picked-btn"
          className={`${isAdmin ? "" : "ml-auto "}inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-[11px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed`}>
          <Download size={12} /> Seçilenleri İndir ({picked.length})
        </button>
        <button onClick={() => setPdfFor("__bulk__")} data-testid="client-bulk-btn"
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold">
          <Download size={12} /> Tümünü İndir (Zip)
        </button>
      </div>
      <div className="max-h-56 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="px-2 py-1.5 w-6">
                <input type="checkbox" checked={allPicked} data-testid="client-pick-all"
                  onChange={() => setPicked(allPicked ? [] : data.summary.map((s) => s.muvekkil))} />
              </th>
              <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Müvekkil</th>
              <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Kayıt</th>
              <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Tahsilat</th>
              <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Gönderilecek</th>
              <th className="px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {data.summary.map((s, i) => (
              <tr key={i} className={`border-b border-slate-100 ${picked.includes(s.muvekkil) ? "bg-emerald-50/60" : ""}`}>
                <td className="px-2 py-1">
                  <input type="checkbox" checked={picked.includes(s.muvekkil)} onChange={() => togglePick(s.muvekkil)} data-testid={`client-pick-${i}`} />
                </td>
                <td className="px-2 py-1 text-slate-900">{s.muvekkil}</td>
                <td className="px-2 py-1 text-right">{s.count}</td>
                <td className="px-2 py-1 text-right font-mono-tab">{fmt(s.tahsilat_total)}</td>
                <td className="px-2 py-1 text-right font-mono-tab font-semibold text-emerald-700">{fmt(s.gonderilecek_total)}</td>
                <td className="px-2 py-1 text-right">
                  <button onClick={() => setPdfFor(s.muvekkil)} data-testid={`client-pdf-btn-${i}`} title={`${s.muvekkil} için PDF`}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-[10px] font-semibold">
                    <FileText size={11} /> PDF / Excel
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pdfFor && <ClientPdfDialog muvekkil={pdfFor.startsWith("__") ? null : pdfFor} selected={pdfFor === "__picked__" ? picked : undefined} year={year} month={month} onClose={() => setPdfFor(null)} />}
    </div>
  );
}


function PersonnelPreview({ data, fmt }) {
  return (
    <div className="divide-y divide-slate-100">
      <div className="p-3 bg-indigo-50 flex items-center gap-4 flex-wrap">
        <Stat label="Personel" value={data.grand.personnel_count} />
        <Stat label="Toplam Prim" value={data.grand.total_count} />
        <Stat label="Onaylı" value={data.grand.approved_count} tone="emerald" />
        <Stat label="Bekleyen" value={data.grand.pending_count} tone="amber" />
        <Stat label="Toplam Prim ₺" value={`${fmt(data.grand.total_prim)} ₺`} big />
      </div>
      <div className="max-h-56 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Personel</th>
              <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Toplam</th>
              <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Onaylı</th>
              <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Prim ₺</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.slice(0, 20).map((r, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="px-2 py-1 text-slate-900">{r.ad_soyad}</td>
                <td className="px-2 py-1 text-right">{r.total_count}</td>
                <td className="px-2 py-1 text-right text-emerald-700 font-semibold">{r.approved_count}</td>
                <td className="px-2 py-1 text-right font-mono-tab font-semibold text-indigo-700">{fmt(r.total_prim)}</td>
              </tr>
            ))}
            {data.rows.length > 20 && (
              <tr><td colSpan={4} className="text-center text-slate-400 py-2 text-[11px]">+{data.rows.length - 20} daha</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function TrendsPreview({ data, fmt }) {
  const g = data.grand;
  const max = Math.max(1, ...data.points.map((p) => p.tahsilat_total));
  return (
    <div>
      <div className="p-3 bg-sky-50 flex items-center gap-4 flex-wrap">
        <Stat label="Süre" value={`Son ${g.months} ay`} />
        <Stat label="Toplam Kayıt" value={g.total_count} />
        <Stat label="Toplam Tahsilat" value={`${fmt(g.total_tahsilat)} ₺`} big />
        <Stat label="Aylık Ort." value={`${fmt(g.avg_monthly_tahsilat)} ₺`} />
        <Stat label="Zirve Ay" value={g.peak_month} tone="sky" />
      </div>
      <div className="p-3">
        <div className="flex items-end gap-1 h-32" data-testid="trends-chart">
          {data.points.map((p, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${p.label}: ${fmt(p.tahsilat_total)} ₺`}>
              <div className="w-full bg-sky-100 rounded-t relative" style={{ height: `${(p.tahsilat_total / max) * 100}%`, minHeight: p.tahsilat_total > 0 ? "4px" : "0" }}>
                <div className="absolute inset-x-0 bottom-0 bg-sky-500 rounded-t" style={{ height: "100%" }}></div>
              </div>
              <div className="text-[9px] text-slate-500 font-mono -rotate-45 origin-top-left mt-1 whitespace-nowrap h-4">
                {p.label.split(" ")[0].slice(0, 3)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function Stat({ label, value, big = false, tone = "slate" }) {
  const toneMap = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    sky: "text-sky-700",
  };
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`${big ? "text-base" : "text-sm"} font-bold ${toneMap[tone] || toneMap.slate}`}>{value ?? "-"}</div>
    </div>
  );
}
